// Minimal line-based markdown for notes: headings, bold/italic/code, links,
// bullet/numbered lists, task lists and blockquotes. Input is HTML-escaped
// first, so preview rendering is safe.

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function inline(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-emerald-700 underline">$1</a>')
    .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-slate-100 text-[0.9em]">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
    .replace(/~~([^~]+)~~/g, '<del>$1</del>')
    .replace(/==([^=]+)==/g, '<mark class="bg-amber-100 px-0.5 rounded">$1</mark>')
}

export interface RenderedMarkdown {
  html: string
  taskCount: number
}

export function renderMarkdown(source: string): RenderedMarkdown {
  const lines = escapeHtml(source).split('\n')
  const out: string[] = []
  let taskCount = 0
  let listType: 'ul' | 'ol' | null = null

  const closeList = () => {
    if (listType) {
      out.push(`</${listType}>`)
      listType = null
    }
  }
  const openList = (type: 'ul' | 'ol') => {
    if (listType !== type) {
      closeList()
      out.push(type === 'ul' ? '<ul class="list-disc pl-6 space-y-1 my-2">' : '<ol class="list-decimal pl-6 space-y-1 my-2">')
      listType = type
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    let m: RegExpMatchArray | null

    if ((m = line.match(/^(#{1,3})\s+(.*)$/))) {
      closeList()
      const level = m[1].length
      const sizes = ['text-xl font-bold', 'text-lg font-bold', 'text-base font-semibold']
      out.push(`<h${level} class="${sizes[level - 1]} text-slate-900 mt-5 mb-2 flex items-center gap-2">${inline(m[2])}</h${level}>`)
      continue
    }
    if (/^\s*(-{3,}|\*{3,})\s*$/.test(line)) {
      closeList()
      out.push('<hr class="my-4 border-slate-200" />')
      continue
    }
    if ((m = line.match(/^\s*&gt;\s?(.*)$/))) {
      closeList()
      out.push(`<blockquote class="border-l-4 border-emerald-400 bg-emerald-50/60 text-emerald-900 rounded-r-md px-4 py-2 my-3">${inline(m[1])}</blockquote>`)
      continue
    }
    if ((m = line.match(/^\s*[-*]\s+\[( |x|X)\]\s+(.*)$/))) {
      closeList()
      const checked = m[1].toLowerCase() === 'x'
      out.push(
        `<label class="flex items-start gap-2 my-1.5 cursor-pointer select-none">` +
          `<input type="checkbox" data-task-index="${taskCount}" ${checked ? 'checked' : ''} class="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 accent-emerald-600" />` +
          `<span class="${checked ? 'line-through text-slate-400' : 'text-slate-700'}">${inline(m[2])}</span>` +
        `</label>`
      )
      taskCount++
      continue
    }
    if ((m = line.match(/^\s*[-*]\s+(.*)$/))) {
      openList('ul')
      out.push(`<li class="text-slate-700">${inline(m[1])}</li>`)
      continue
    }
    if ((m = line.match(/^\s*\d+\.\s+(.*)$/))) {
      openList('ol')
      out.push(`<li class="text-slate-700">${inline(m[1])}</li>`)
      continue
    }
    closeList()
    if (line.trim() === '') {
      out.push('<div class="h-2"></div>')
    } else {
      out.push(`<p class="text-slate-700 leading-relaxed my-1">${inline(line)}</p>`)
    }
  }
  closeList()
  return { html: out.join('\n'), taskCount }
}

// Toggles the Nth `- [ ]` / `- [x]` marker in the markdown source.
export function toggleTaskInSource(source: string, taskIndex: number): string {
  let seen = -1
  return source
    .split('\n')
    .map((line) => {
      const m = line.match(/^(\s*[-*]\s+\[)([ xX])(\].*)$/)
      if (!m) return line
      seen++
      if (seen !== taskIndex) return line
      const next = m[2].toLowerCase() === 'x' ? ' ' : 'x'
      return `${m[1]}${next}${m[3]}`
    })
    .join('\n')
}

export interface NoteTask {
  index: number
  text: string
  done: boolean
  indent: number
}

const TASK_LINE = /^(\s*)[-*]\s+\[([ xX])\]\s+(.*)$/

// Splits markdown source into checklist tasks (with the task-index the
// toggle helper expects) and the remaining prose.
export function parseTasks(source: string): { tasks: NoteTask[]; rest: string } {
  const tasks: NoteTask[] = []
  const restLines: string[] = []
  for (const line of (source || '').split('\n')) {
    const m = line.match(TASK_LINE)
    if (m) {
      tasks.push({
        index: tasks.length,
        text: m[3],
        done: m[2].toLowerCase() === 'x',
        indent: Math.floor(m[1].replace(/\t/g, '  ').length / 2),
      })
    } else {
      restLines.push(line)
    }
  }
  return { tasks, rest: restLines.join('\n').trim() }
}

export function addTaskToSource(source: string, text: string): string {
  const s = (source || '').trimEnd()
  return s ? `${s}\n- [ ] ${text}` : `- [ ] ${text}`
}

// Inserts a subtask indented one level under the given task.
export function addSubtaskToSource(source: string, parentTaskIndex: number, text: string): string {
  const lines = (source || '').split('\n')
  let seen = -1
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(TASK_LINE)
    if (!m) continue
    seen++
    if (seen !== parentTaskIndex) continue
    lines.splice(i + 1, 0, `${m[1]}  - [ ] ${text}`)
    return lines.join('\n')
  }
  return addTaskToSource(source, text)
}

// ── Toolbar helpers: apply a markdown transform to the textarea selection ───

export type MarkdownAction =
  | 'bold' | 'italic' | 'strike' | 'code' | 'h1' | 'h2' | 'h3'
  | 'bullet' | 'numbered' | 'checklist' | 'link' | 'quote'

export interface ApplyResult {
  value: string
  selectionStart: number
  selectionEnd: number
}

export function applyMarkdownAction(
  action: MarkdownAction,
  fullValue: string,
  selStart: number,
  selEnd: number
): ApplyResult {
  const selected = fullValue.slice(selStart, selEnd)

  const wrap = (before: string, after = before, placeholder = 'text') => {
    const inner = selected || placeholder
    const value = fullValue.slice(0, selStart) + before + inner + after + fullValue.slice(selEnd)
    return { value, selectionStart: selStart + before.length, selectionEnd: selStart + before.length + inner.length }
  }

  const prefixLines = (prefix: (i: number) => string) => {
    const inner = selected || 'List item'
    const lines = inner.split('\n')
    const transformed = lines.map((l, i) => {
      const stripped = l.replace(/^(#{1,3}\s|[-*]\s\[[ xX]\]\s|[-*]\s|\d+\.\s|>\s)/, '')
      return prefix(i) + stripped
    }).join('\n')
    const value = fullValue.slice(0, selStart) + transformed + fullValue.slice(selEnd)
    return { value, selectionStart: selStart, selectionEnd: selStart + transformed.length }
  }

  switch (action) {
    case 'bold': return wrap('**')
    case 'italic': return wrap('*')
    case 'strike': return wrap('~~')
    case 'code': return wrap('`')
    case 'h1': return prefixLines(() => '# ')
    case 'h2': return prefixLines(() => '## ')
    case 'h3': return prefixLines(() => '### ')
    case 'bullet': return prefixLines(() => '- ')
    case 'numbered': return prefixLines((i) => `${i + 1}. `)
    case 'checklist': return prefixLines(() => '- [ ] ')
    case 'quote': return prefixLines(() => '> ')
    case 'link': {
      const inner = selected || 'link text'
      const inserted = `[${inner}](https://)`
      const value = fullValue.slice(0, selStart) + inserted + fullValue.slice(selEnd)
      const urlStart = selStart + inner.length + 3
      return { value, selectionStart: urlStart, selectionEnd: urlStart + 8 }
    }
  }
}
